/**
 * Trygghetsboxen UF — Interaction Engine v3.0
 *
 * Modules:
 *   1.  Feature detection (reduced-motion, iOS)
 *   2.  Hero: staggered load animation
 *   3.  Hero: parallax image scroll effect
 *   4.  Hero: inject arrow span into CTA buttons
 *   5.  Header: scroll-depth class + transparent→frosted transition
 *   6.  Nav: active link marking
 *   7.  Scroll reveal (IntersectionObserver, fires once)
 *   8.  Product image fade-in + skeleton
 *   9.  Button: tactile press feedback (pointerdown)
 *   10. Forms: shake on validation error
 *   11. Shopify theme editor live-reload hook
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     1. Feature Detection
     ───────────────────────────────────────────── */
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  // P0.1 FIX: .page-wrapper is the scroll container only on desktop (≥990px)
  // where base.css gives it `height: 100dvh; overflow-y: auto`.
  // On mobile (<990px), html/body scroll normally via window.
  // Using the wrong container caused ALL scroll-based features (header depth,
  // floating CTA visibility, parallax) to silently break on mobile.
  function resolveScrollContainer() {
    const pw = document.querySelector('.page-wrapper');
    if (!pw) return window;
    // Check if page-wrapper is actually scrollable (desktop mode)
    const style = getComputedStyle(pw);
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') return pw;
    return window;
  }
  const SCROLL_CONTAINER = resolveScrollContainer();

  function getScrollY() {
    return SCROLL_CONTAINER === window
      ? window.scrollY
      : SCROLL_CONTAINER.scrollTop;
  }

  /* ─────────────────────────────────────────────
     2. Hero: Staggered Load Animation
     ───────────────────────────────────────────── */
  function initHeroStagger() {
    if (REDUCED_MOTION) return;

    const hero = document.querySelector('.hero');
    if (!hero) return;

    // Gather meaningful visible text/interactive elements in hero overlay
    const selectors = [
      '.hero__container h1, .hero__container .h1',
      '.hero__container h2, .hero__container .h2',
      '.hero__container h3, .hero__container .h3',
      '.hero__container p:not(:empty)',
      '.hero__container [class*="button-unstyled"]',
      '.hero__container .button',
      '.hero__container a[href]',
    ].join(',');

    const elements = [...hero.querySelectorAll(selectors)];

    // Deduplicate — only take the first 5 unique elements
    const seen = new Set();
    const targets = elements.filter((el) => {
      if (seen.has(el)) return false;
      seen.add(el);
      return true;
    }).slice(0, 5);

    targets.forEach((el, i) => {
      el.style.setProperty('--tb-stagger', String(i));
      el.classList.add('tb-hero-animate');
    });
  }

  /* ─────────────────────────────────────────────
     3. Hero: Parallax Scroll
     ───────────────────────────────────────────── */
  function initHeroParallax() {
    // Disable on iOS and mobile/tablet under 1024px (transform issues with touch scroll)
    if (REDUCED_MOTION || IS_IOS || window.innerWidth < 1024) return;

    const hero = document.querySelector('.hero');
    if (!hero) return;

    // Target the actual background image element
    const mediaImg =
      hero.querySelector('.hero__media img') ||
      hero.querySelector('[class*="hero__media"] img') ||
      hero.querySelector('.hero__media-wrapper img');

    if (!mediaImg) return;

    let raf = null;
    const heroHeight = hero.offsetHeight;

    const tick = () => {
      const sy = getScrollY();
      if (sy > heroHeight) { raf = null; return; }

      // Shift up to 60px downward as user scrolls; scale gives bleed room
      const shift = sy * 0.22;
      mediaImg.style.transform = `scale(1.08) translateY(${shift}px)`;
      raf = null;
    };

    SCROLL_CONTAINER.addEventListener(
      'scroll',
      () => {
        if (!raf) raf = requestAnimationFrame(tick);
      },
      { passive: true }
    );
  }

  /* ─────────────────────────────────────────────
     4. Hero: Arrow Span Injection
     ───────────────────────────────────────────── */
  function initHeroArrows() {
    const ARROW_RE = /(→|>|›|»|▶|\-\>|&gt;)/g;

    document
      .querySelectorAll(
        '.hero [class*="button-unstyled"], .hero .button, .hero a[class*="button"]'
      )
      .forEach((btn) => {
        if (btn.querySelector('.tb-btn-arrow')) return; // already done

        // Only wrap text nodes, preserve child elements
        btn.childNodes.forEach((node) => {
          if (node.nodeType !== Node.TEXT_NODE) return;
          if (!ARROW_RE.test(node.textContent)) return;

          const span = document.createElement('span');
          span.innerHTML = node.textContent.replace(
            ARROW_RE,
            '<span class="tb-btn-arrow">$1</span>'
          );
          node.replaceWith(...span.childNodes);
        });
      });
  }

  /* ─────────────────────────────────────────────
     5. Header: Scroll Depth
     ───────────────────────────────────────────── */
  function initHeaderDepth() {
    const header =
      document.querySelector('#header-component') ||
      document.querySelector('.shopify-section.header-section');
    if (!header) return;

    const THRESHOLD = 40;
    let ticking = false;

    const update = () => {
      const scrolled = getScrollY() > THRESHOLD;
      header.classList.toggle('tb-header--scrolled', scrolled);
      // Also toggle on the header section wrapper for specificity
      header.closest('.shopify-section')
            ?.classList.toggle('tb-header--scrolled', scrolled);
      ticking = false;
    };

    SCROLL_CONTAINER.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );

    update(); // run once on init
  }

  /* ─────────────────────────────────────────────
     6. Nav: Active Link
     ───────────────────────────────────────────── */
  function initActiveNav() {
    const path = window.location.pathname;

    document
      .querySelectorAll(
        '.header-menu__item > a, .header__menu-item > a'
      )
      .forEach((link) => {
        try {
          const url = new URL(link.href, window.location.origin);
          if (url.pathname === path && path !== '/') {
            link
              .closest('li, .header-menu__item, .header__menu-item')
              ?.classList.add('tb-nav-active');
            link.setAttribute('aria-current', 'page');
          }
        } catch (_) { /* noop */ }
      });
  }

  /* ─────────────────────────────────────────────
     7. Scroll Reveal
     ───────────────────────────────────────────── */
  function initScrollReveal() {
    const SELECTORS = [
      '.tb-reveal',
      '.product-card',
      '.section-resource-list',
      'footer',
      '.footer',
      '.section-footer',
      '.tb-card',
      '.tb-team-card',
      '.tb-value-card',
      '.tb-om-hero',
      '.tb-stat',
      '.tb-partner-card',
      '.tb-timeline__item',
      '.tb-cta-band',
      '.tb-gear-card',
      '.tb-gear-header',
      '.tb-gear-inspector'
    ].join(', ');

    const targets = document.querySelectorAll(SELECTORS);
    if (!targets.length) return;

    if (REDUCED_MOTION) {
      targets.forEach((el) => {
        el.classList.remove('tb-reveal--pending');
        el.classList.add('tb-in-view');
      });
      return;
    }

    const scrollContainer = document.querySelector('.page-wrapper');
    const scrollRoot = window.innerWidth >= 990 && scrollContainer ? scrollContainer : null;

    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry, idx) => {
          if (!entry.isIntersecting) return;
          if (entries.length > 1) {
            entry.target.style.transitionDelay = `${Math.min(idx * 60, 240)}ms`;
          }
          entry.target.classList.remove('tb-reveal--pending');
          entry.target.classList.add('tb-in-view');
          obs.unobserve(entry.target);
        });
      },
      { root: scrollRoot, rootMargin: '100px 0px 60px 0px', threshold: 0.01 }
    );

    targets.forEach((el) => {
      const rect = el.getBoundingClientRect();
      // If already within or near visible viewport, reveal immediately
      if (rect.top < window.innerHeight * 1.05 && rect.bottom > -50) {
        el.classList.remove('tb-reveal--pending');
        el.classList.add('tb-in-view');
        return;
      }

      el.classList.add('tb-reveal', 'tb-reveal--pending');
      io.observe(el);
    });
  }

  /* ─────────────────────────────────────────────
     8. Product & Team Image Fade-in + Skeleton
     ───────────────────────────────────────────── */
  function initImageFade() {
    document
      .querySelectorAll('.product-card img, .card img, .tb-team-card__img, img[loading="lazy"]')
      .forEach((img) => {
        // Add skeleton/fade class to parent/img until loaded
        const wrapper = img.parentElement;

        const reveal = () => {
          img.classList.remove('tb-img-fade');
          img.classList.add('tb-img-loaded');
          wrapper?.classList.remove('tb-img-loading');
        };

        if (img.complete && img.naturalWidth > 0) {
          reveal();
        } else {
          img.classList.add('tb-img-fade');
          wrapper?.classList.add('tb-img-loading');
          img.addEventListener('load', reveal, { once: true });
          img.addEventListener('error', reveal, { once: true });
        }
      });
  }

  /* ─────────────────────────────────────────────
     9. Button Tactile Press
     ───────────────────────────────────────────── */
  function initButtonPress() {
    if (REDUCED_MOTION) return;

    const BTN_SEL =
      '.button, .button-secondary, .button-custom, .quick-add__button, [class*="button-unstyled"]';

    const press = (e) => {
      const btn = e.target.closest(BTN_SEL);
      if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      btn.classList.add('tb-pressed');
    };

    const release = (e) => {
      const btn = e.target.closest(BTN_SEL);
      if (!btn) {
        // Release on any up in case pointer left element
        document.querySelectorAll('.tb-pressed').forEach((b) => b.classList.remove('tb-pressed'));
        return;
      }
      btn.classList.remove('tb-pressed');
    };

    document.body.addEventListener('pointerdown', press);
    document.body.addEventListener('pointerup', release);
    document.body.addEventListener('pointercancel', () =>
      document.querySelectorAll('.tb-pressed').forEach((b) => b.classList.remove('tb-pressed'))
    );
  }

  /* ─────────────────────────────────────────────
     10. Form Shake on Error
     ───────────────────────────────────────────── */
  function initFormShake() {
    // Listen for Shopify form error events or mutation-based error injection
    const observeErrors = () => {
      document.querySelectorAll('form').forEach((form) => {
        form.addEventListener('submit', (e) => {
          const invalids = form.querySelectorAll(':invalid');
          if (invalids.length > 0) {
            invalids.forEach((field) => {
              field.classList.remove('tb-field-error');
              // Force reflow to restart animation
              void field.offsetWidth;
              field.classList.add('tb-field-error');
              field.addEventListener(
                'animationend',
                () => field.classList.remove('tb-field-error'),
                { once: true }
              );
            });
          }
        });
      });
    };

    observeErrors();

    // Also handle Shopify's ajax form errors
    document.addEventListener('shopify:section:load', observeErrors);
  }

  /* ─────────────────────────────────────────────
     10b. 3D Tilt Effect on Desktop
     ───────────────────────────────────────────── */
  function init3DTilt() {
    if (REDUCED_MOTION) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const tiltElements = document.querySelectorAll(
      '.hero__media, .hero__media-wrapper, .product-card, .tb-inspector-card'
    );

    tiltElements.forEach((el) => {
      if (el.dataset.tiltInitialized) return;
      el.dataset.tiltInitialized = 'true';

      let isHovered = false;
      let rafId = null;
      let targetX = 0;
      let targetY = 0;
      let currentX = 0;
      let currentY = 0;

      el.style.transformStyle = 'preserve-3d';
      el.style.willChange = 'transform';

      const updateTransform = () => {
        if (!isHovered) {
          currentX += (0 - currentX) * 0.15;
          currentY += (0 - currentY) * 0.15;
          if (Math.abs(currentX) < 0.01 && Math.abs(currentY) < 0.01) {
            currentX = 0;
            currentY = 0;
            el.style.transform = '';
            rafId = null;
            return;
          }
        } else {
          currentX += (targetX - currentX) * 0.12;
          currentY += (targetY - currentY) * 0.12;
        }

        const maxTilt = el.classList.contains('product-card') ? 5 : 4;
        const rotateX = (-currentY * maxTilt).toFixed(2);
        const rotateY = (currentX * maxTilt).toFixed(2);
        const scale = isHovered ? 1.015 : 1;

        el.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(${scale}, ${scale}, ${scale})`;

        rafId = requestAnimationFrame(updateTransform);
      };

      el.addEventListener('mouseenter', () => {
        isHovered = true;
        if (!rafId) rafId = requestAnimationFrame(updateTransform);
      });

      el.addEventListener('mousemove', (e) => {
        const rect = el.getBoundingClientRect();
        targetX = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
        targetY = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
        if (!rafId) rafId = requestAnimationFrame(updateTransform);
      });

      el.addEventListener('mouseleave', () => {
        isHovered = false;
        targetX = 0;
        targetY = 0;
      });
    });
  }

  /* ─────────────────────────────────────────────
     10c. Floating Mobile CTA Button
     ───────────────────────────────────────────── */
  function initMobileFloatingCTA() {
    const path = window.location.pathname.toLowerCase();
    const isCommercePage = path === '/' || path === '' || path.includes('/products') || path.includes('/collections') || path.includes('/cart');
    
    let floatingWrap = document.querySelector('.tb-mobile-floating-cta');
    if (isCommercePage) {
      if (floatingWrap) floatingWrap.remove();
      return;
    }

    if (!floatingWrap) {
      floatingWrap = document.createElement('div');
      floatingWrap.className = 'tb-mobile-floating-cta';
      floatingWrap.innerHTML = `
        <a href="/collections/all" class="tb-mobile-floating-cta__button" id="tb-mobile-cta-btn">
          <span>Handla nu</span>
          <span class="tb-mobile-floating-cta__icon">→</span>
        </a>
      `;
      document.body.appendChild(floatingWrap);
    }

    let isTicking = false;

    const checkVisibility = () => {
      if (window.innerWidth > 768) {
        floatingWrap.classList.remove('is-visible');
        isTicking = false;
        return;
      }

      // Never show floating CTA on pages where products and buy buttons already exist
      const path = window.location.pathname.toLowerCase();
      if (path === '/' || path === '' || path.includes('/products') || path.includes('/collections') || path.includes('/cart')) {
        floatingWrap.classList.remove('is-visible');
        isTicking = false;
        return;
      }

      const sy = getScrollY();
      const scrollHeight = SCROLL_CONTAINER === window
        ? (document.documentElement.scrollHeight || document.body.scrollHeight)
        : SCROLL_CONTAINER.scrollHeight;
      const clientHeight = SCROLL_CONTAINER === window ? window.innerHeight : SCROLL_CONTAINER.clientHeight;
      const maxScroll = scrollHeight - clientHeight;
      const nearBottom = maxScroll > 0 && sy > (maxScroll - 160);

      if (sy > 260 && !nearBottom) {
        floatingWrap.classList.add('is-visible');
      } else {
        floatingWrap.classList.remove('is-visible');
      }
      isTicking = false;
    };

    window.addEventListener('scroll', () => {
      if (!isTicking) {
        requestAnimationFrame(checkVisibility);
        isTicking = true;
      }
    }, { passive: true });

    if (SCROLL_CONTAINER !== window) {
      SCROLL_CONTAINER.addEventListener('scroll', () => {
        if (!isTicking) {
          requestAnimationFrame(checkVisibility);
          isTicking = true;
        }
      }, { passive: true });
    }

    window.addEventListener('resize', checkVisibility, { passive: true });
    checkVisibility();
  }

  /* ─────────────────────────────────────────────
     12. P1.4 — Desktop Hero Cursor Glow
     Subtle radial glow that follows cursor on hero image.
     Only runs on desktop (pointer: fine) / non-reduced-motion.
     ───────────────────────────────────────────── */
  function initHeroCursorGlow() {
    if (REDUCED_MOTION) return;
    if (!window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)').matches) return;

    const hero = document.querySelector('.hero');
    if (!hero) return;

    const media = hero.querySelector('.hero__media, [class*="hero__media"]');
    if (!media) return;

    // Create glow overlay
    let glowEl = media.querySelector('.tb-cursor-glow');
    if (!glowEl) {
      glowEl = document.createElement('div');
      glowEl.className = 'tb-cursor-glow';
      glowEl.style.cssText = [
        'position: absolute',
        'inset: 0',
        'pointer-events: none',
        'z-index: 3',
        'border-radius: inherit',
        'background: radial-gradient(ellipse 380px 280px at 50% 50%, rgba(93,176,232,0.12) 0%, transparent 70%)',
        'opacity: 0',
        'transition: opacity 400ms ease',
        'mix-blend-mode: screen',
      ].join(';');

      // Make sure media has relative positioning
      const curPos = getComputedStyle(media).position;
      if (curPos === 'static') media.style.position = 'relative';
      media.appendChild(glowEl);
    }

    let rafId = null;
    let targetX = 50, targetY = 50;
    let curX = 50, curY = 50;

    const lerp = (a, b, t) => a + (b - a) * t;

    const animate = () => {
      curX = lerp(curX, targetX, 0.08);
      curY = lerp(curY, targetY, 0.08);
      glowEl.style.background = `radial-gradient(ellipse 380px 280px at ${curX}% ${curY}%, rgba(93,176,232,0.12) 0%, transparent 70%)`;
      if (Math.abs(curX - targetX) > 0.1 || Math.abs(curY - targetY) > 0.1) {
        rafId = requestAnimationFrame(animate);
      } else {
        rafId = null;
      }
    };

    hero.addEventListener('mousemove', (e) => {
      const rect = hero.getBoundingClientRect();
      targetX = ((e.clientX - rect.left) / rect.width) * 100;
      targetY = ((e.clientY - rect.top) / rect.height) * 100;
      glowEl.style.opacity = '1';
      if (!rafId) rafId = requestAnimationFrame(animate);
    }, { passive: true });

    hero.addEventListener('mouseleave', () => {
      glowEl.style.opacity = '0';
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    });
  }

  /* ─────────────────────────────────────────────
     11. Init & Shopify Editor Hooks
     ───────────────────────────────────────────── */
  function init() {
    initHeroStagger();
    initHeroParallax();
    initHeroArrows();
    initHeaderDepth();
    initActiveNav();
    initScrollReveal();
    initImageFade();
    initButtonPress();
    initFormShake();
    init3DTilt();
    initMobileFloatingCTA();
    initHeroCursorGlow();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor live reload hooks
  document.addEventListener('shopify:section:load', () => {
    initScrollReveal();
    initActiveNav();
    initHeroStagger();
    initHeroArrows();
    initImageFade();
    initFormShake();
    init3DTilt();
    initMobileFloatingCTA();
    initHeroCursorGlow();
  });

  document.addEventListener('shopify:section:reorder', initScrollReveal);
})();
